import { getListingCatalogReqSSR, getSeoResolveSSR } from '@/features/seo-listing/api';
import type {
  CatalogFilterPageKind,
  ParsedListingFilters,
  SeoResolveData,
} from '@/features/seo-listing/types';
import { buildPermalinkFilterOptionsParams } from './build-catalog-filter-fetch-plan.util';
import { buildPermalinkListingFilters } from './build-permalink-listing-filters.util';
import { fetchFanficsFilterOptionsCached } from './fetch-fanfics-filter-options.cached';
import type { FanficsFilterOptions } from './fetch-fanfics-filter-options.util';
import type { RawFanficListItem } from '@/features/seo-listing/api/listing-catalog.requests.ssr';

interface LoadPermalinkListingPageParams {
  permalink: string;
  fanficsQueryParams: URLSearchParams;
  pageKind: Exclude<CatalogFilterPageKind, 'catalog'>;
  type: 'fanfic' | 'original';
}

export type PermalinkListingPageData =
  | {
      kind: 'ok';
      resolved: SeoResolveData;
      filters: ParsedListingFilters | null;
      fanfics: RawFanficListItem[];
      totalPages: number;
      totalItems: number;
      filterOptions: FanficsFilterOptions;
    }
  | { kind: 'catalog_error' }
  | { kind: 'unresolved' };

/**
 * Loads SEO permalink listing data without a catalog→filters waterfall.
 * Resolve starts first so filter dictionaries can overlap with the remaining catalog wait.
 */
export const loadPermalinkListingPage = async ({
  permalink,
  fanficsQueryParams,
  pageKind,
  type,
}: LoadPermalinkListingPageParams): Promise<PermalinkListingPageData> => {
  const catalogPromise = getListingCatalogReqSSR(fanficsQueryParams.toString());
  const resolveResult = await getSeoResolveSSR(permalink);
  const resolved = resolveResult.status === 'ok' ? resolveResult.data : null;

  if (!resolved) {
    await catalogPromise;
    return { kind: 'unresolved' };
  }

  const filters = buildPermalinkListingFilters(resolved, permalink);
  const filterOptionsPromise = fetchFanficsFilterOptionsCached(
    buildPermalinkFilterOptionsParams(pageKind, type, filters),
  );

  const [catalogResult, filterOptions] = await Promise.all([catalogPromise, filterOptionsPromise]);

  if (catalogResult.kind === 'error') {
    return { kind: 'catalog_error' };
  }

  return {
    kind: 'ok',
    resolved,
    filters,
    fanfics: catalogResult.fanfics,
    totalPages: catalogResult.totalPages,
    totalItems: catalogResult.totalItems,
    filterOptions,
  };
};
